import { useCallback, useEffect, useState, type FormEvent } from "react";

import { api, type Regimen, type Template } from "@/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorAlert } from "@/components/error-alert";
import { PageSkeleton } from "@/components/page-skeleton";
import { cn } from "@/lib/utils";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

type DraftEntry = { key: number; templateId: string; time: string };
type Draft = { name: string; entries: DraftEntry[] };

let keySeq = 1;
const newEntry = (templateId = "", time = "09:00"): DraftEntry => ({
  key: keySeq++,
  templateId,
  time,
});

function draftOf(r: Regimen | null): Draft {
  if (!r) return { name: "", entries: [newEntry()] };
  return {
    name: r.name,
    entries: r.entries.map((e) => newEntry(e.template_id, e.start_time)),
  };
}

export function RegimensPage() {
  const [regimens, setRegimens] = useState<Regimen[] | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<"new" | Regimen | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, lib] = await Promise.all([api.regimens(), api.templates()]);
      setRegimens(r.regimens);
      setTemplates(lib.templates);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load Regimens"));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !regimens) return <ErrorAlert message={error} />;
  if (!regimens) return <PageSkeleton />;

  const templateById = new Map(templates.map((t) => [t.id, t]));

  return (
    <div className="grid gap-4">
      {error && <ErrorAlert message={error} />}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Regimens</CardTitle>
            <CardDescription>
              A Regimen is a day&apos;s basic events at fixed times, like Work,
              Lunch and Dinner. In the Planner, apply it to the days of a week
              to fill them in one go.
            </CardDescription>
          </div>
          <Button onClick={() => setEditing(editing === "new" ? null : "new")}>
            {editing === "new" ? "Close" : "New Regimen"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {editing !== null && (
            <RegimenEditor
              key={editing === "new" ? "new" : editing.id}
              regimen={editing === "new" ? null : editing}
              templates={templates}
              onClose={() => setEditing(null)}
              onSaved={async () => {
                setEditing(null);
                await load();
              }}
            />
          )}
          {regimens.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No Regimens yet. Create your first one.
            </p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {regimens.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className={cn(
                      "w-full rounded-lg border bg-card p-3 text-left shadow-sm transition-colors hover:bg-secondary/60",
                      editing !== null &&
                        editing !== "new" &&
                        editing.id === r.id
                        ? "border-primary"
                        : "border-border",
                    )}
                  >
                    <span className="block truncate text-sm font-medium">
                      {r.name}
                    </span>
                    <ol className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                      {r.entries.map((e) => (
                        <li key={e.id ?? e.template_id + e.start_time} className="flex gap-2">
                          <span className="tabular-nums">{e.start_time}</span>
                          <span className="min-w-0 flex-1 truncate">
                            {templateById.get(e.template_id)?.title ?? "?"}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RegimenEditor({
  regimen,
  templates,
  onClose,
  onSaved,
}: {
  regimen: Regimen | null;
  templates: Template[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(regimen));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setEntry(key: number, patch: Partial<DraftEntry>) {
    setDraft((d) => ({
      ...d,
      entries: d.entries.map((e) => (e.key === key ? { ...e, ...patch } : e)),
    }));
  }
  function pickTemplate(key: number, templateId: string) {
    const t = templates.find((x) => x.id === templateId);
    setEntry(key, {
      templateId,
      ...(t?.suggested_start ? { time: t.suggested_start } : {}),
    });
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const body = {
      name: draft.name.trim(),
      entries: draft.entries
        .filter((e) => e.templateId !== "")
        .map((e) => ({ template_id: e.templateId, start_time: e.time })),
    };
    setBusy(true);
    setError(null);
    try {
      if (regimen) await api.updateRegimen(regimen.id, body);
      else await api.createRegimen(body);
      await onSaved();
    } catch (err) {
      setError(messageOf(err, "could not save Regimen"));
    }
    setBusy(false);
  }

  async function handleDelete() {
    if (!regimen) return;
    if (!window.confirm(`Delete the Regimen "${regimen.name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteRegimen(regimen.id);
      await onSaved();
    } catch (err) {
      setError(messageOf(err, "could not delete Regimen"));
    }
    setBusy(false);
  }

  return (
    <form
      className="rounded-xl border border-border bg-secondary/40 p-4"
      onSubmit={(ev) => void handleSubmit(ev)}
    >
      <p className="text-sm font-medium">
        {regimen ? `Edit "${regimen.name}"` : "New Regimen"}
      </p>
      <div className="mt-3 grid max-w-sm gap-1.5">
        <Label htmlFor="regimen-name">Name</Label>
        <Input
          id="regimen-name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Work day"
          required
        />
      </div>

      <div className="mt-4">
        <span className="text-sm font-medium">Events</span>
        <ol className="mt-2 space-y-2">
          {draft.entries.map((e, i) => (
            <li key={e.key} className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={`Event ${i + 1} start time`}
                className="w-28"
                type="time"
                value={e.time}
                onChange={(ev) => setEntry(e.key, { time: ev.target.value })}
                required
              />
              <Select
                value={e.templateId}
                onValueChange={(v) => pickTemplate(e.key, v)}
              >
                <SelectTrigger
                  aria-label={`Event ${i + 1} template`}
                  className="min-w-48 flex-1"
                >
                  <SelectValue placeholder="Pick a Template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() =>
                  setDraft((d) => ({
                    ...d,
                    entries: d.entries.filter((x) => x.key !== e.key),
                  }))
                }
                aria-label="Remove event"
              >
                ✕
              </Button>
            </li>
          ))}
        </ol>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-2"
          onClick={() =>
            setDraft({ ...draft, entries: [...draft.entries, newEntry()] })
          }
        >
          Add event
        </Button>
        {templates.length === 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            Create Templates first (Work, Lunch, Dinner); a Regimen places them.
          </p>
        )}
      </div>

      {error && (
        <div className="mt-3">
          <ErrorAlert message={error} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="submit" disabled={busy}>
          {regimen ? "Save Regimen" : "Create Regimen"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </Button>
        {regimen && (
          <Button
            type="button"
            variant="ghost"
            className="ml-auto text-destructive"
            onClick={() => void handleDelete()}
            disabled={busy}
          >
            Delete
          </Button>
        )}
      </div>
    </form>
  );
}
